import { useQuery } from 'react-query'
import { useAuth0 } from '../../contexts/auth0-context'
import endpoints from '../../API/endpoints'
import callAPI from '../../API/callSecureAPI'

export type StrategyTier = 'readOnly' | 'commentOnly' | 'full' | 'roadmapAdmin'

const FIVE_MINUTES = 1000 * 60 * 5

// GET /strategy/:id is already the one place a roadmap gets loaded before any of its edit
// affordances render, and its response now carries myTier (see server/controllers/strategy-
// controller.js) -- reusing it here means this piggybacks on an existing, already-gated,
// already-cached-per-strategyId fetch instead of adding a second endpoint.
export const useMyStrategyTierQuery = (strategyId?: string | null) => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    /**
     * Throws rather than answering null when the tier could not be read: null means the server
     * told us this user holds nothing on the roadmap, and RoadmapSelector deselects the roadmap on
     * it. A request that never got an answer must not look like that.
     */
    const getMyTier = async (): Promise<StrategyTier | null> => {
        const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

        // refetch() ignores `enabled`, so this runs on mount while auth0 is still starting up.
        if (!token) {
            throw new Error('Not authenticated yet.')
        }

        const response = await callAPI(endpoints.getStrategy.url + strategyId, endpoints.getStrategy.method, token)

        // callSecureAPI resolves for 401/403/500 alike, so an unsuccessful body is the only sign
        // the request failed at all.
        if (!response?.success) {
            throw new Error(response?.message || 'Could not read roadmap access.')
        }

        return response.data?.myTier ?? null
    }

    return useQuery<StrategyTier | null>(['strategy', strategyId, 'myTier'], getMyTier, {
        enabled: !!strategyId && !!getTokenSilently && !!isAuthenticated && !isLoading,
        cacheTime: FIVE_MINUTES,
        staleTime: FIVE_MINUTES,
        retry: 1,
    })
}
